import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  IconButton,
} from "@mui/material";
import { Close, Save } from "@mui/icons-material";
import theme from "../../theme/theme";
import customerLabel from "../../utils/customerLabel";
import { fetchCustomerPage } from "../../api/CustomerService";
import AsyncAutocomplete from "../common/AsyncAutocomplete";

const today = () => new Date().toISOString().split("T")[0];
const toDateInput = (val) => (val ? String(val).slice(0, 10) : "");

const emptyForm = () => ({
  customer_id: "",
  invoice_id: null,
  letter_date: today(),
  project_location: "",
  total_price: "",
  number_of_lifts: 1,
  lift_capacity: "",
  reference_no: "",
  handover_date: "",
  warranty_start: "",
  warranty_end: "",
});

/**
 * Create / edit a handover letter.
 * - `letter`  : when set, the dialog is in edit mode and seeds from it.
 * - `prefill` : { customer_id, total_price, invoice_id } used by the
 *               "generate from invoice" flow to pre-populate the form.
 */
const CreateHandoverLetterDialog = ({
  open,
  onClose,
  saving,
  onSave,
  errors = {},
  customers = [],
  letter = null,
  prefill = null,
}) => {
  const isEdit = Boolean(letter);
  const [form, setForm] = useState(emptyForm());

  // Full object for the current customer_id so the label renders even before
  // the dropdown's pages have loaded (edit mode / invoice prefill).
  const selectedCustomerOption =
    (letter?.customer?.id === form.customer_id ? letter.customer : null) ||
    (prefill?.customer?.id === form.customer_id ? prefill.customer : null) ||
    customers.find((c) => c.id === form.customer_id) ||
    null;

  useEffect(() => {
    if (!open) return;
    if (letter) {
      setForm({
        customer_id: letter.customer_id ?? "",
        invoice_id: letter.invoice_id ?? null,
        letter_date: toDateInput(letter.letter_date) || today(),
        project_location: letter.project_location ?? "",
        total_price: letter.total_price ?? "",
        number_of_lifts: letter.number_of_lifts ?? 1,
        lift_capacity: letter.lift_capacity ?? "",
        reference_no: letter.reference_no ?? "",
        handover_date: toDateInput(letter.handover_date),
        warranty_start: toDateInput(letter.warranty_start),
        warranty_end: toDateInput(letter.warranty_end),
      });
    } else {
      setForm({ ...emptyForm(), ...(prefill || {}) });
    }
  }, [open, letter, prefill]);

  const setField = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSave = () => {
    onSave({
      customer_id: form.customer_id,
      invoice_id: form.invoice_id || null,
      letter_date: form.letter_date,
      project_location: form.project_location.trim(),
      total_price: parseFloat(form.total_price) || 0,
      number_of_lifts: parseInt(form.number_of_lifts, 10) || 1,
      lift_capacity: form.lift_capacity.trim(),
      reference_no: form.reference_no.trim() || null,
      handover_date: form.handover_date,
      warranty_start: form.warranty_start,
      warranty_end: form.warranty_end,
    });
  };

  const err = (field) => (errors[field] ? errors[field][0] : "");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: theme.dialog.borderRadius, overflow: "hidden" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          fontWeight: 700,
        }}
      >
        {isEdit ? "Edit Handover Letter" : "Create Handover Letter"}
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Row 1: customer + letter date */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <AsyncAutocomplete
            label="Customer"
            placeholder="Search customer..."
            value={form.customer_id}
            onChange={(id) => setField("customer_id", id)}
            fetchPage={fetchCustomerPage}
            getOptionLabel={customerLabel}
            selectedOption={selectedCustomerOption}
            error={!!err("customer_id")}
            helperText={err("customer_id")}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            type="date"
            label="Letter Date"
            InputLabelProps={{ shrink: true }}
            value={form.letter_date}
            onChange={(e) => setField("letter_date", e.target.value)}
            error={!!err("letter_date")}
            helperText={err("letter_date")}
            sx={{ width: 200 }}
          />
        </Box>

        {/* Row 2: project location + total price */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            size="small"
            label="Project Location"
            value={form.project_location}
            onChange={(e) => setField("project_location", e.target.value)}
            error={!!err("project_location")}
            helperText={err("project_location")}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            type="number"
            label="Total Price (₹)"
            value={form.total_price}
            onChange={(e) => setField("total_price", e.target.value)}
            error={!!err("total_price")}
            helperText={err("total_price")}
            inputProps={{ min: 0 }}
            sx={{ width: 200 }}
          />
        </Box>

        {/* Row 3: no. of lifts + capacity + reference */}
        <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
          <TextField
            size="small"
            type="number"
            label="No. of Lifts"
            value={form.number_of_lifts}
            onChange={(e) => setField("number_of_lifts", e.target.value)}
            error={!!err("number_of_lifts")}
            helperText={err("number_of_lifts")}
            inputProps={{ min: 1 }}
            sx={{ width: 140 }}
          />
          <TextField
            size="small"
            label="Lift Capacity"
            placeholder="e.g. 6 Passenger"
            value={form.lift_capacity}
            onChange={(e) => setField("lift_capacity", e.target.value)}
            error={!!err("lift_capacity")}
            helperText={err("lift_capacity")}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            label="Reference No"
            placeholder="e.g. RBL/AP/25-26"
            value={form.reference_no}
            onChange={(e) => setField("reference_no", e.target.value)}
            error={!!err("reference_no")}
            helperText={err("reference_no")}
            sx={{ flex: 1 }}
          />
        </Box>

        {/* Row 4: handover date + warranty start/end */}
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            size="small"
            type="date"
            label="Handover Date"
            InputLabelProps={{ shrink: true }}
            value={form.handover_date}
            onChange={(e) => setField("handover_date", e.target.value)}
            error={!!err("handover_date")}
            helperText={err("handover_date")}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            type="date"
            label="Warranty Start"
            InputLabelProps={{ shrink: true }}
            value={form.warranty_start}
            onChange={(e) => setField("warranty_start", e.target.value)}
            error={!!err("warranty_start")}
            helperText={err("warranty_start")}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            type="date"
            label="Warranty End"
            InputLabelProps={{ shrink: true }}
            value={form.warranty_end}
            onChange={(e) => setField("warranty_end", e.target.value)}
            error={!!err("warranty_end")}
            helperText={err("warranty_end")}
            sx={{ flex: 1 }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={saving}
          variant="outlined"
          sx={{
            borderRadius: theme.button.borderRadius,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          startIcon={<Save />}
          sx={{
            borderRadius: theme.button.borderRadius,
            textTransform: "none",
            fontWeight: 600,
            backgroundColor: theme.palette.primary.main,
          }}
        >
          {saving
            ? "Saving..."
            : isEdit
              ? "Update Letter"
              : "Create Letter"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateHandoverLetterDialog;
